/**
 * Receipt detail page orchestrator — document-first layout mirroring
 * invoice/bill detail. Loads `receipts.get`, `invoices.get` (for the FX
 * context + invoice number), and `companies.get` (for letterhead), then
 * composes the header + ribbon + doc-stage + rail.
 *
 * @see prototype/sales/invoice-detail.html — visual peer
 * @see docs/adr/0019-fx-invoice-receipt-redesign.md
 */
import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Alert, PromptDialog, toast } from "@flint/ui";
import { trpc } from "../../../lib/trpc.ts";
import { meQueryOptions } from "../../../lib/session.ts";
import { openDocPdf } from "../../../lib/pdf.ts";
import { ReceiptDetailHeader } from "./receipt-detail-header.tsx";
import { ReceiptVoucher } from "./receipt-voucher.tsx";
import { ReceiptRail } from "./receipt-rail.tsx";
import "../receipt-detail.css";

export interface ReceiptDetailPageProps {
  receiptId: string;
}

export function ReceiptDetailPage({ receiptId }: ReceiptDetailPageProps) {
  // Typed loosely so the page compiles regardless of which routers are
  // wired into the AppRouter at any given time (the dev typecheck baseline
  // has cascading union errors when api-contract is out of sync — this
  // keeps the page resilient without leaking `any` into rendering code).
  const api = trpc as typeof trpc & Record<string, any>;
  const utils = trpc.useUtils();
  const navigate = useNavigate();
  const [voidOpen, setVoidOpen] = useState(false);
  const me = useQuery(meQueryOptions);
  const receiptQuery = api.receipts.get.useQuery({ id: receiptId });
  const company = api.companies.get.useQuery({});

  const voidMutation = api.receipts.void.useMutation({
    onSuccess: async () => {
      toast.success("Receipt voided", {
        description: "The ledger posting was reversed and invoice balances restored.",
      });
      await Promise.all([
        utils.invalidate(),
        receiptQuery.refetch(),
      ]);
      navigate({ to: "/sales/receipts" });
    },
    onError: (e: { message: string }) =>
      toast.error("Couldn't void receipt", { description: e.message }),
  });

  const firstAllocation = receiptQuery.data?.receipt.allocations[0];
  const invoiceQuery = api.invoices.get.useQuery(
    { invoiceId: firstAllocation?.invoiceId ?? "" },
    { enabled: !!firstAllocation },
  );

  if (receiptQuery.isLoading || company.isLoading) {
    return (
      <div className="receipt-detail-page">
        <p className="text-sm" style={{ color: "var(--muted)" }}>
          Loading receipt…
        </p>
      </div>
    );
  }
  if (receiptQuery.error || !receiptQuery.data) {
    return (
      <div className="receipt-detail-page">
        <Alert
          intent="bad"
          title="Couldn't load receipt"
          description={receiptQuery.error?.message ?? "Receipt not found."}
        />
      </div>
    );
  }
  if (company.error || !company.data) {
    return (
      <div className="receipt-detail-page">
        <Alert
          intent="bad"
          title="Couldn't load company profile"
          description={
            company.error?.message ??
            "Settings → Company profile is required for the receipt voucher."
          }
        />
      </div>
    );
  }

  const { receipt, customer } = receiptQuery.data;
  const invoice = invoiceQuery.data?.invoice ?? null;
  const isFounder = me.data?.company?.role === "Founder";
  const isVoided = !!receiptQuery.data.isVoided;

  return (
    <div className="receipt-detail-page">
      <ReceiptDetailHeader
        receipt={{
          id: receipt.id,
          receivedOn: receipt.receivedOn,
          amountPaise: receipt.amountPaise,
          voidsId: receipt.voidsId,
        }}
        invoice={invoice ? { id: invoice.id, docNumber: invoice.docNumber } : null}
        customer={{ name: customer.name }}
        isFounder={isFounder}
        isVoided={isVoided}
        pendingVoid={voidMutation.isPending}
        onPrint={() => openDocPdf(`/api/receipts/${receipt.id}/pdf`)}
        onDownloadPdf={() =>
          openDocPdf(`/api/receipts/${receipt.id}/pdf`, {
            download: true,
            filename: `receipt-${receipt.id.slice(0, 8)}`,
          })
        }
        onVoid={() => setVoidOpen(true)}
      />

      {/* Ribbon removed — status already lives in the header pill and
       *  the FX rate context lives in the voucher's `.doc-totals` stack. */}

      <div className="doc-stage">
        <ReceiptVoucher
          receipt={{
            id: receipt.id,
            receivedOn: receipt.receivedOn,
            amountPaise: receipt.amountPaise,
            tdsPaise: receipt.tdsPaise,
            fxGainLossPaise: receipt.fxGainLossPaise,
            settlementRatePaise: receipt.settlementRatePaise,
            tdsSectionSnapshot: receipt.tdsSectionSnapshot,
            mode: receipt.mode,
            reference: receipt.reference,
            allocations: receipt.allocations,
            settlementLines: receipt.settlementLines,
            voidsId: receipt.voidsId,
          }}
          customer={{ name: customer.name, tdsSection: customer.tdsSection }}
          invoice={
            invoice
              ? {
                  id: invoice.id,
                  docNumber: invoice.docNumber,
                  currencyCode: invoice.currencyCode,
                  foreignAmountMinor: invoice.foreignAmountMinor,
                  fxRateToInrPaise: invoice.fxRateToInrPaise,
                  fxRateDate: invoice.fxRateDate,
                }
              : null
          }
          company={{
            legalName: company.data.legalName,
            address: company.data.address ?? null,
            city: company.data.city ?? null,
            state: company.data.state ?? null,
            pincode: company.data.pincode ?? null,
            gstin: company.data.gstin ?? null,
            bankName: company.data.bankName ?? null,
            bankAccountNumber: company.data.bankAccountNumber ?? null,
            bankIfsc: company.data.bankIfsc ?? null,
          }}
        />
        <ReceiptRail
          receipt={receipt}
          customer={customer}
          invoice={
            invoice
              ? { id: invoice.id, docNumber: invoice.docNumber, currencyCode: invoice.currencyCode }
              : null
          }
        />
      </div>

      {/* Reserved space — a timeline / linked-strip lands in a follow-up
       *  once `audit_entries` exposes a per-receipt feed in the wire. */}

      <PromptDialog
        open={voidOpen}
        onOpenChange={setVoidOpen}
        title="Void this receipt?"
        description="The ledger posting is reversed and each allocated invoice's balance is restored. This is append-only — the void is a new entry, not a deletion."
        label="Reason"
        placeholder="e.g. Duplicate entry — settled under RCPT-0042"
        intent="danger"
        multiline
        confirmLabel="Void receipt"
        pending={voidMutation.isPending}
        onConfirm={(reason) => voidMutation.mutate({ id: receipt.id, reason })}
      />
    </div>
  );
}
